// Bandeau d'alerte visible en haut du dashboard restaurateur quand l'imprimante
// Bluetooth n'est pas connectée. Cliquer sur le bouton relance l'appairage.
// Ne modifie PAS btPrinter.js — utilise uniquement getPrinterStatus() et connectBluetoothPrinter().
import { useEffect, useState } from "react";
import { Printer, Loader2, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { getPrinterStatus, connectBluetoothPrinter, testDisconnectAlert, unlockAudioAlert } from "../lib/btPrinter";

export default function PrinterAlert() {
  const [connected, setConnected] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Rafraîchit UNIQUEMENT si l'état change réellement — évite re-render inutiles qui
    // provoquent un "flash" perçu sur tablettes Android bas de gamme.
    const refresh = () => {
      try {
        const s = getPrinterStatus();
        const newConnected = !!s.connected;
        const newName = s.name || "";
        setConnected((prev) => (prev === newConnected ? prev : newConnected));
        setName((prev) => (prev === newName ? prev : newName));
      } catch {
        setConnected((prev) => (prev === false ? prev : false));
      }
    };
    refresh();
    // Polling ralenti de 2s → 15s (le statut Bluetooth ne bouge quasi jamais en usage réel).
    // Arrêté quand la page est cachée (verrouillée / onglet inactif) — moins de charge CPU
    // et surtout plus de re-render intempestif au réveil sur tablette Android.
    let iv = null;
    const start = () => { if (iv == null) iv = setInterval(refresh, 15000); };
    const stop = () => { if (iv != null) { clearInterval(iv); iv = null; } };
    const onVis = () => { if (document.hidden) stop(); else { refresh(); start(); } };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  // Web Bluetooth non supporté (tablette Archos avec WebView obsolète, iOS Safari…) :
  // pas d'alerte — l'utilisateur ne peut pas résoudre le problème dans l'app.
  if (typeof navigator === "undefined" || !("bluetooth" in navigator)) return null;

  const onPair = async () => {
    if (busy) return;
    setBusy(true);
    unlockAudioAlert();
    try {
      await connectBluetoothPrinter();
      const s = getPrinterStatus();
      setConnected(!!s.connected);
      setName(s.name || "");
      toast.success(`Imprimante appairée${s.name ? ` : ${s.name}` : ""}`);
    } catch (e) {
      toast.error(e?.message || "Appairage impossible.");
    } finally { setBusy(false); }
  };

  const onTestVoice = () => {
    testDisconnectAlert();
    toast.info("Test alerte vocale en cours…");
  };

  // Imprimante CONNECTÉE : petit chip discret pour tester la voix sans devoir
  // débrancher l'imprimante (indispensable pour valider l'audio sur la tablette).
  if (connected) {
    return (
      <div
        data-testid="rt-printer-voice-test-chip"
        className="mt-4 rounded-xl bg-[#F4FBF7] border border-[#B7E4C7] p-2.5 flex items-center gap-2 justify-end"
      >
        <div className="text-xs text-[#2E5D3B] font-semibold mr-auto">
          🖨️ Imprimante {name ? `« ${name} »` : ""} connectée
        </div>
        <button
          data-testid="rt-printer-alert-test-voice"
          onClick={onTestVoice}
          className="h-8 px-3 rounded-full bg-white border border-[#2E5D3B] text-[#2E5D3B] hover:bg-[#EAF6EE] font-bold text-xs inline-flex items-center gap-1.5"
          title="Vérifier que la voix + la sirène fonctionnent sur cet appareil"
        >
          <Volume2 className="w-3.5 h-3.5" />
          Tester la voix
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="rt-printer-alert"
      role="alert"
      className="mt-4 rounded-2xl bg-[#FFF3E6] border-2 border-[#F26419] p-4 flex items-center gap-3 flex-wrap sm:flex-nowrap"
    >
      <div className="w-10 h-10 rounded-full bg-[#F26419] text-white grid place-items-center shrink-0">
        <Printer className="w-5 h-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-head font-black text-sm text-[#7A3A00]">Imprimante non connectée</div>
        <div className="text-xs text-[#7A3A00]/80 mt-0.5">
          Les tickets ne s'imprimeront pas tant que la Bluetooth n'est pas appairée. Cliquez pour reconnecter.
        </div>
      </div>
      <button
        data-testid="rt-printer-alert-test-voice"
        onClick={onTestVoice}
        className="h-10 px-4 rounded-full bg-white border-2 border-[#F26419] text-[#F26419] hover:bg-[#FFF3E6] font-bold text-xs inline-flex items-center gap-2 shrink-0"
        title="Vérifier que la voix + la sirène fonctionnent sur cet appareil"
      >
        <Volume2 className="w-4 h-4" />
        Tester la voix
      </button>
      <button
        data-testid="rt-printer-alert-pair"
        onClick={onPair}
        disabled={busy}
        className="h-10 px-4 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold text-xs inline-flex items-center gap-2 disabled:opacity-60 shrink-0"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
        {busy ? "Appairage…" : "Reconnecter"}
      </button>
    </div>
  );
}
